import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  SEO_CONFIG, 
  PAGE_SEO, 
  getOrganizationStructuredData,
  getProductStructuredData,
  getServiceStructuredData,
  getBreadcrumbStructuredData,
  getArticleStructuredData
} from '../../config/seo';

/**
 * SEO Component for dynamic meta tags and structured data
 * Updates document title and meta tags based on current route
 * 
 * @param {string} title - Custom page title (optional)
 * @param {string} description - Custom page description (optional)
 * @param {string} keywords - Custom keywords (optional)
 * @param {string} image - Custom image URL (optional)
 * @param {Object} structuredData - Additional structured data (product, service, article, breadcrumbs)
 */
const SEO = ({ title, description, keywords, image, structuredData }) => {
  const location = useLocation();
  
  // Determine page type from pathname
  const getPageType = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path.startsWith('/products')) return 'products';
    if (path.startsWith('/services')) return 'services';
    if (path.startsWith('/about')) return 'about';
    if (path.startsWith('/contact')) return 'contact';
    if (path.startsWith('/blogs')) return 'blogs';
    if (path.startsWith('/testimonials')) return 'testimonials';
    return 'home';
  };

  const pageType = getPageType();
  const pageSEO = PAGE_SEO[pageType] || PAGE_SEO.home;
  
  // Use provided props or fall back to page-specific SEO
  const finalTitle = title || pageSEO.title || SEO_CONFIG.defaultTitle;
  const finalDescription = description || pageSEO.description || SEO_CONFIG.defaultDescription;
  const finalKeywords = keywords || pageSEO.keywords || SEO_CONFIG.keywords.join(', ');
  const finalImage = image || SEO_CONFIG.defaultImage;

  useEffect(() => {
    // Update document title
    document.title = finalTitle;

    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', finalDescription);

    // Update or create meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', finalKeywords);

    // Update Open Graph tags
    const updateOGTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    updateOGTag('og:title', finalTitle);
    updateOGTag('og:description', finalDescription);
    updateOGTag('og:url', `${SEO_CONFIG.siteUrl}${location.pathname}`);
    updateOGTag('og:image', `${SEO_CONFIG.siteUrl}${finalImage}`);

    // Update Twitter Card tags
    const updateTwitterTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    updateTwitterTag('twitter:title', finalTitle);
    updateTwitterTag('twitter:description', finalDescription);
    updateTwitterTag('twitter:image', `${SEO_CONFIG.siteUrl}${finalImage}`);

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SEO_CONFIG.siteUrl}${location.pathname}`);

    // Add structured data (JSON-LD) for organization
    const addStructuredData = (data, id) => {
      let script = document.querySelector(`script[type="application/ld+json"][data-seo="${id}"]`);
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-seo', id);
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };

    // Always add organization structured data
    addStructuredData(getOrganizationStructuredData(), 'organization');

    // Add additional structured data if provided
    if (structuredData) {
      if (structuredData.product) {
        const productUrl = `${SEO_CONFIG.siteUrl}${location.pathname}`;
        addStructuredData(getProductStructuredData(structuredData.product, productUrl), 'product');
    }
      if (structuredData.service) {
        const serviceUrl = `${SEO_CONFIG.siteUrl}${location.pathname}`;
        addStructuredData(getServiceStructuredData(structuredData.service, serviceUrl), 'service');
      }
      if (structuredData.article) {
        const articleUrl = `${SEO_CONFIG.siteUrl}${location.pathname}`;
        addStructuredData(getArticleStructuredData(structuredData.article, articleUrl), 'article');
      }
      if (structuredData.breadcrumbs) {
        addStructuredData(getBreadcrumbStructuredData(structuredData.breadcrumbs), 'breadcrumbs');
      }
    }

    // Clean up old structured data that's no longer needed
    const activeIds = ['organization'];
    if (structuredData?.product) activeIds.push('product');
    if (structuredData?.service) activeIds.push('service');
    if (structuredData?.article) activeIds.push('article');
    if (structuredData?.breadcrumbs) activeIds.push('breadcrumbs');

    const allStructuredDataScripts = document.querySelectorAll('script[type="application/ld+json"][data-seo]');
    allStructuredDataScripts.forEach(script => {
      const id = script.getAttribute('data-seo');
      if (!activeIds.includes(id)) {
        script.remove();
      }
    });

  }, [finalTitle, finalDescription, finalKeywords, finalImage, location.pathname, structuredData]);

  return null; // This component doesn't render anything
};

export default SEO;
